import { useCallback, useMemo } from 'react';
import { useDebounce } from 'src/hooks/use-debounce';
import {
  isPortalSyncNotConfiguredError,
  useGetPortalWorkspacesQuery,
  useUpdatePortalSubscriptionTierMutation,
  useUpdatePortalWorkspaceBulkSettingsMutation,
} from '../endpoints/portal-users.endpoints';
import { useUsersTabUrlParams } from './use-users-tab-url-params';
import type {
  AdminSettableSubscriptionTier,
  PortalUserSubscriptionTier,
  SubscriptionFilter,
} from '../types/users.types';

const DEFAULT_PAGE_SIZE = 25;

export function useFlowTeams() {
  const {
    search,
    setSearch,
    subscriptionFilter,
    setSubscriptionFilter,
    page,
    setPage,
  } = useUsersTabUrlParams();

  const debouncedSearch = useDebounce(search, 500);

  const queryParams = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    subscription: subscriptionFilter === 'all' ? undefined : subscriptionFilter,
    page,
    page_size: DEFAULT_PAGE_SIZE,
  }), [debouncedSearch, subscriptionFilter, page]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPortalWorkspacesQuery(queryParams);

  const [updateBulkSettings] = useUpdatePortalWorkspaceBulkSettingsMutation();
  const [updateSubscriptionTier] = useUpdatePortalSubscriptionTierMutation();

  const isPortalSyncNotConfigured = isError && isPortalSyncNotConfiguredError(error);

  const applyBulkSettings = useCallback(async(
    workspaceId: string,
    settings: {
      creative_quota_override?: number | null;
      disable_billing_conversion?: boolean;
      hide_conversion_modal?: boolean;
      tier?: AdminSettableSubscriptionTier;
    },
  ) => {
    const bulkPayload: {
      creative_quota_override?: number | null;
      disable_billing_conversion?: boolean;
      hide_conversion_modal?: boolean;
    } = {};

    if ('creative_quota_override' in settings) {
      bulkPayload.creative_quota_override = settings.creative_quota_override ?? null;
    }
    if ('disable_billing_conversion' in settings) {
      bulkPayload.disable_billing_conversion = settings.disable_billing_conversion;
    }
    if ('hide_conversion_modal' in settings) {
      bulkPayload.hide_conversion_modal = settings.hide_conversion_modal;
    }

    try {
      if (Object.keys(bulkPayload).length > 0) {
        await updateBulkSettings({ workspaceId, ...bulkPayload }).unwrap();
      }

      if (settings.tier) {
        await updateSubscriptionTier({
          workspaceId,
          tier: settings.tier as PortalUserSubscriptionTier,
        }).unwrap();
      }

      return { ok: true as const };
    } catch (mutationError: unknown) {
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const message = typeof responseData?.message === 'string'
        ? responseData.message
        : typeof responseData?.detail === 'string'
          ? responseData.detail
          : 'Failed to apply team-wide settings.';
      return { ok: false as const, message };
    }
  }, [updateBulkSettings, updateSubscriptionTier]);

  return {
    teams: data?.results ?? [],
    totalCount: data?.count ?? 0,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    setPage,
    search,
    setSearch,
    subscriptionFilter,
    setSubscriptionFilter: setSubscriptionFilter as (value: SubscriptionFilter) => void,
    isLoading: isLoading || isFetching,
    isError,
    isPortalSyncNotConfigured,
    error,
    refetch,
    applyBulkSettings,
    actionsDisabled: isPortalSyncNotConfigured,
  };
}
